import { accountIdLocalStorage } from "../utilities/storage.js";
import { authToken, AppData } from "../stores.js";
import { get } from "svelte/store";
import { getAPIURL } from "../config.js";

// default headers to apply to all requests...
const headers = {
    Accept: "application/json",
    "Content-Type": "application/json",
    Origin: "",
};
const LOG_VERBOSE = true;
const LOG = (msg, transport = (msg) => console.log(msg)) => {
    if (LOG_VERBOSE) {
        transport(msg);
    }
};
/*
* fetcher is a wrapper for the native Fetch API.
*
*

@statusHandlers: dictionary mapping response status codes to functions to call
when that status code occurs.


// TODO -- maybe only call onError / onSuccess if statusHandlers not specified
// or maybe remove them here entirely and check elsewhere :)
*/
const fetcher = ({
    method,
    route,
    payload = null,
    onSuccess = null,
    onError = null,
    statusHandlers = null,
    successStatus = [200, 204],
}) => {
    const API_URL = getAPIURL();
    LOG(
        `fetcher: host:${API_URL} method:${method},\nroute:${route}\npayload:${JSON.stringify(
            payload
        )}\nonSuccess:${onSuccess}\nonFailure:${onError}\nstatusHandlers:${statusHandlers}\nsuccessStatuses:${JSON.stringify(
            successStatus
        )}`
    );
    const fetchObj = {
        method,
        headers: {
            ...headers,
            "Content-Type": "application/json",
            Authorization: `Bearer ${get(authToken)["access_token"]}`,
        },
    };
    console.log("fetchObj headers:", fetchObj);
    const result = {};
    if (payload !== null) {
        fetchObj.body = JSON.stringify(payload);
    }
    return fetch(`${API_URL}${route}`, fetchObj)
        .then((data) => {
            console.log("fetched data", data);
            result.status = data.status;
            if (successStatus.indexOf(data.status) === -1) {
                // TODO -- put optional "fail on / retry on" statuses here.
                throw new Error(data);
            } else {
                return data;
            }
        })
        .then(async (parsed) => {
            let maybeJSON = {}
            try {
                maybeJSON = await parsed.json();
            } catch(err) {
                console.log("no json", err);
            }
            result.data = maybeJSON;
            result.err = null;
            result.success = true;
            if (statusHandlers !== null && typeof statusHandlers[result.status] === "function") {
                console.log("calling status handler success", result.status);
                return statusHandlers[result.status](result);
            }
            if (typeof onSuccess === "function") {
                return onSuccess(result);
            }
            return result;
        })
        .catch((err) => {
            console.log("catch err", err);
            // TODO -- put optional retry here.
            let didTrap = false;
            result.data = null;
            result.err = err;
            result.success = false;
            if (statusHandlers !== null && typeof statusHandlers[result.status] === "function") {
                didTrap = true;
                console.log("calling status handler failure", result.status);
                return statusHandlers[result.status](result);
            }
            if (typeof onError === "function") {
                didTrap = true;
                return onError(result);
            }

            if (!didTrap) {
                console.warn("Err trap, unhandled exception:", JSON.stringify(err));
            }
            return {
                data: null,
                error: err,
                success: false,
            };
        });
};

const fetchAccount = ({ onSuccess, onError, statusHandlers }) =>
    fetcher({
        method: "GET",
        route: "/account",
        onSuccess,
        onError,
        statusHandlers,
    }).then((result) => Promise.resolve(result));

const logOut = ({ onSuccess, onError }) =>
    fetcher({
        method: "POST",
        route: `/logout`,
        onSuccess,
        onError,
    }).then((result) => Promise.resolve(result));

const refreshAuthToken = ({ onSuccess, onError, statusHandlers }) =>
    fetcher({
        method: "POST",
        route: `/oauth/token`,
        payload: {
            refresh_token: get(authToken).refresh_token,
            grant_type: "refresh_token",
        },
        statusHandlers,
        onSuccess,
        onError,
    });

const exchangeDelegationCodeForAuthToken = ({ code, onSuccess, onError, statusHandlers }) =>
    fetcher({
        method: "POST",
        route: `/auth-delegate/token`,
        payload: { code },
        statusHandlers,
        onSuccess,
        onError,
    });

const setNewsletterPreference = ({ newsLetterPreference }) =>
    fetcher({
        method: "PATCH",
        route: `/account/profile`,
        payload: {
            newsletter: newsLetterPreference,
        },
    }).then((result) => Promise.resolve(result));

const handleLoginEmailSubmit = ({ data, onSuccess, onError }) =>
    fetcher({
        method: "POST",
        route: "/login/email",
        payload: data,
        onSuccess,
        onError,
    });

// handleCodeSubmit sends the code the user enters that was e-mailed to their inbox
// to the server, along with the initial e-mail response. The initial e-mail response
// contains @data.nonce -- an xsrf token to make it difficult to guess codes.
const handleLoginCodeSubmit = ({ data, onSuccess, onError, statusHandlers }) =>
    fetcher({
        method: "POST",
        route: `/login/email/confirm`,
        payload: data,
        statusHandlers,
        onSuccess,
        onError,
    });

/**
 * Email update a [updateEmail]): PATCH accounts:/keys/:id with new email, returns code ID
 * Email update b [updateEmailConfirm]): POST to accounts:/keys/:id with code ID, code key, returns new auth info including new OAuth2 token.
 * The existing OAuth2 token for the current login session will be revoked. The auth info may also contain other
 * information that will not be relevant to the web app.
 */
const updateEmail = ({ dataToUpdate, onSuccess, onError, statusHandlers }) =>
    fetcher({
        method: "PATCH",
        route: `/account/keys/${get(AppData).uuid}`,
        payload: dataToUpdate,
        statusHandlers,
        onSuccess,
        onError,
    });

const updateEmailConfirm = ({ dataToUpdate, onSuccess, onError, statusHandlers }) =>
    fetcher({
        method: "POST",
        route: `/account/keys/${get(AppData).uuid}`,
        payload: dataToUpdate,
        statusHandlers,
        onSuccess,
        onError,
    });

const updateDevice = ({ device_id, dataToUpdate, onSuccess, onError, statusHandlers }) =>
    fetcher({
        method: "PATCH",
        route: `/vpn/devices/${device_id}`,
        payload: dataToUpdate,
        statusHandlers,
        onSuccess,
        onError,
    });

const deleteDevice = ({ device_id, dataToUpdate, onSuccess, onError, statusHandlers }) =>
    fetcher({
        method: "DELETE",
        route: `/vpn/devices/${device_id}`,
        payload: dataToUpdate,
        statusHandlers,
        onSuccess,
        onError,
    });

const cancelSubscriptionForProduct = ({ id, onSuccess, onError, statusHandlers }) =>
    fetcher({
        method: "DELETE",
        route: `/account/product/${id}`,
        statusHandlers,
        onSuccess,
        onError,
    });

const emailVPNDownloadLinks = ({ onSuccess, onError, statusHandlers }) =>
    fetcher({
        method: "POST",
        route: "/vpn/download-links/email",
        statusHandlers,
        onSuccess,
        onError,
    });

export {
    fetchAccount,
    logOut,
    setNewsletterPreference,
    handleLoginEmailSubmit,
    handleLoginCodeSubmit,
    updateDevice,
    deleteDevice,
    updateEmail,
    updateEmailConfirm,
    cancelSubscriptionForProduct,
    emailVPNDownloadLinks,
    refreshAuthToken,
    exchangeDelegationCodeForAuthToken,
};
